<template>
    <SidebarPanel
        :is-open="ui.search.opened"
        :title="localizations[lang].searchTitle"
        side="right"
        persistent
        mobile-fullscreen
        @close="toggleSearch"
    >
        <div dusk="MenuSearch" class="flex flex-col gap-2">
            <div class="flex w-full items-center rounded-lg border-2 border-theme-border bg-theme-bg p-0 pl-3">
                <svg-icon icon="magnifying-glass" class="text-theme-toolbar-icon" width="20px" height="20px" />
                <input
                    ref="searchInput"
                    v-model="searchTarget"
                    dusk="searchInput"
                    :aria-label="localizations[lang].tooltips.searchInBook"
                    :placeholder="localizations[lang].tooltips.searchInBook"
                    class="w-full appearance-none border-none bg-transparent py-1.5 pl-2 pr-0.5 text-sm text-theme-text outline-none selection:bg-theme-accent selection:text-theme-accent-on focus-visible:ring-2 focus-visible:ring-theme-accent"
                    type="search"
                    @input="handleInput"
                    @keydown="handleKeyNavigation"
                />
            </div>

            <div v-if="searchTarget.length > 0" class="m-0 p-0" dusk="MenuSearchResult">
                <ul
                    v-if="withResults && !isBelowMinLength"
                    ref="list"
                    aria-live="polite"
                    aria-relevant="additions removals"
                    class="mt-1 w-full overflow-y-auto rounded-lg border-2 border-theme-border bg-theme-bg p-1"
                >
                    <li
                        v-for="(result, index) in searchResults"
                        :key="index"
                        ref="resultItems"
                        role="button"
                        tabindex="0"
                        class="cursor-pointer rounded p-2 hover:bg-theme-icon-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                        :class="{
                            'bg-theme-icon-bg': selectedIndex === index,
                        }"
                        @click="goToPageOrLocation(result)"
                        @mouseenter="selectedIndex = index"
                    >
                        <span v-if="!isEpub" class="m-0 block p-0 text-xs uppercase text-theme-toolbar-icon">
                            {{ localizations[lang].page }} {{ result.location.value }}
                        </span>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div
                            :lang="issue.lang"
                            class="m-0 p-0 text-xs text-theme-toolbar-icon"
                            v-html="result.snippet"
                        />
                    </li>
                </ul>
                <div
                    v-else
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    class="mt-1 flex w-full items-center justify-around rounded-lg border-2 border-theme-border bg-theme-bg px-4 py-2 text-center"
                >
                    <div v-if="isBelowMinLength" class="text-sm text-theme-toolbar-icon">
                        {{ localizations[lang].keepTyping }}
                    </div>
                    <div v-else-if="isEmpty" class="text-sm text-theme-text">
                        {{ localizations[lang].searchResultEmpty }}
                    </div>
                    <div v-else class="m-auto flex items-center gap-3">
                        <Spinner width="15px" height="15px" />
                        <div class="text-sm text-theme-text">
                            {{ localizations[lang].searchLoading }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="searchedWords.length > 0" class="m-0 p-0">
                <ul class="mt-1 w-full rounded-lg border-2 border-theme-border bg-theme-bg p-1">
                    <li class="m-0 py-1 pb-0 pl-3 pr-0 text-xs text-theme-toolbar-icon">
                        {{ localizations[lang].lastSearch }}
                    </li>
                    <li
                        v-for="(word, index) in searchedWords"
                        :key="index"
                        ref="historyItems"
                        role="button"
                        tabindex="0"
                        class="flex cursor-pointer flex-row items-center py-2 pl-3 pr-2 text-xs text-theme-text hover:bg-theme-icon-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                        :class="{
                            'bg-theme-icon-bg': selectedIndex === index,
                        }"
                        @click="handleInput(word)"
                        @mouseenter="selectedIndex = index"
                    >
                        <svg-icon icon="magnifying-glass" width="20px" height="20px" />
                        <div class="inline-block px-2">{{ word }}</div>
                    </li>
                </ul>
            </div>
        </div>
    </SidebarPanel>
</template>

<script>
'use strict';

import { debounce } from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import Spinner from '../Utilities/SimpleSpinner.vue';
import SidebarPanel from './SidebarPanel.vue';
import { searchAPI } from '../../../src/js/communication';

export default {
    name: 'ReaderSearch',
    components: {
        Spinner,
        SidebarPanel,
    },
    data() {
        return {
            searchTarget: '',
            oldSearchTarget: '',
            searchResults: [],
            loading: false,
            isBelowMinLength: false,
            searchedWords: [],
            selectedIndex: -1,
        };
    },
    computed: {
        ...mapState(['issue', 'title', 'uiHidden', 'ui', 'localizations', 'lang', 'device', 'context']),
        ...mapGetters(['isEpub']),
        canBeclearInput() {
            return this.searchTarget === this.oldSearchTarget && this.searchTarget.length !== 0;
        },
        noSearchResult() {
            return this.searchResults.length === 0 && this.oldSearchTarget.length > 0;
        },
        isEmpty() {
            return this.noSearchResult && !this.loading;
        },
        withResults() {
            return !this.noSearchResult && this.searchResults.length > 0;
        },
    },
    watch: {
        uiHidden() {
            this.pauseOrResumeReaderNavigation();
        },
        'ui.search.opened'(isActive) {
            if (isActive) {
                this.trackEvent({
                    action: 'Search - open',
                    trigger: 'click',
                    source: 'search',
                });
                this.selectedIndex = -1;
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (this.$refs.searchInput) this.$refs.searchInput.focus();
                    }, 350);
                });
            } else {
                this.selectedIndex = -1;
            }
        },
    },
    mounted() {
        this.loadLocalStorage();
    },
    updated() {
        Vue.nextTick().then(() => {
            this.searchFocus();
            this.clearWhenIsClosing();
            this.pauseOrResumeReaderNavigation();
        });
    },
    methods: {
        ...mapMutations([
            'pauseKeyboardNavigation',
            'resumeKeyboardNavigation',
            'requestQueryWordsHighlight',
            'fulfillQueryWordsHighlight',
            'requestClearWordsHighlight',
        ]),
        ...mapActions(['toggleSearch', 'goToPage', 'trackEvent', 'goToLocation']),
        loadLocalStorage() {
            try {
                const storedWords = localStorage?.getItem('searchedWords');
                if (storedWords) {
                    this.searchedWords = JSON.parse(storedWords);
                }
            } catch (_error) {
                this.searchedWords = [];
            }
        },
        addToSearchHistory(search) {
            search = search.toLocaleLowerCase();
            const existingIndex = this.searchedWords.indexOf(search);
            if (existingIndex !== -1) {
                this.searchedWords.splice(existingIndex, 1);
            }
            this.searchedWords.unshift(search);
            if (this.searchedWords.length > 5) {
                this.searchedWords.pop();
            }
            localStorage.setItem('searchedWords', JSON.stringify(this.searchedWords));
        },

        clearInput() {
            this.searchTarget = '';
            this.isBelowMinLength = false;
            this.requestClearWordsHighlight(true);
            this.searchResults = [];
            this.selectedIndex = -1;
        },

        clearWhenIsClosing() {
            if (this.noSearchResult && !this.ui.search.opened) {
                this.oldSearchTarget = '';
                this.clearInput();
            }
        },

        // While the search box is on screen, the arrows move its caret, not the book.
        pauseOrResumeReaderNavigation() {
            if (this.ui.search.opened && !this.uiHidden) {
                this.pauseKeyboardNavigation('search');
            } else {
                this.resumeKeyboardNavigation('search');
            }
        },

        searchFocus(_stop = false) {
            if (this.$refs.searchInput) this.$refs.searchInput.focus();

            if (this.ui.search.opened) {
                document.addEventListener('keydown', this.handleShortcutKeyFocus);
            } else {
                document.removeEventListener('keydown', this.handleShortcutKeyFocus);
            }
        },

        handleShortcutKeyFocus(e) {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
                (e.preventDefault(), this.$refs.searchInput.focus());
                this.trackEvent({
                    action: 'Search - focus',
                    trigger: 'shortcut',
                    source: 'search',
                });
            }
        },

        handleInput(text) {
            if (typeof text === 'string') {
                this.searchTarget = text;
            }
            this.isBelowMinLength = false;
            this.searchResults = [];
            this.selectedIndex = -1;
            if (this.searchTarget.length === 0) return;

            // Only validate minimum length. Max length (128) is intentionally not validated
            // client-side - we let long searches run and return "no results" naturally for
            // better UX instead of showing an error message.
            if (this.searchTarget.length < 4) {
                this.isBelowMinLength = true;
                return;
            }

            this.loading = true;
            this.performSearch();
        },

        handleKeyNavigation(e) {
            const hasResults = this.searchResults.length > 0;
            const hasHistory = this.searchedWords.length > 0 && this.searchTarget.length === 0;
            const totalItems = hasResults ? this.searchResults.length : hasHistory ? this.searchedWords.length : 0;

            if (totalItems === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (this.selectedIndex === -1) {
                        this.selectedIndex = 0;
                        this.scrollToSelected();
                    } else if (this.selectedIndex < totalItems - 1) {
                        this.selectedIndex = this.selectedIndex + 1;
                        this.scrollToSelected();
                    }
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    if (this.selectedIndex === -1) {
                        return;
                    } else if (this.selectedIndex === 0) {
                        this.selectedIndex = -1;
                        this.$refs.searchInput.focus();
                    } else {
                        this.selectedIndex = this.selectedIndex - 1;
                        this.scrollToSelected();
                    }
                    break;

                case 'Enter':
                    e.preventDefault();
                    if (this.selectedIndex >= 0) {
                        if (hasResults) {
                            this.goToPageOrLocation(this.searchResults[this.selectedIndex]);
                        } else if (hasHistory) {
                            this.handleInput(this.searchedWords[this.selectedIndex]);
                        }
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    if (this.selectedIndex >= 0) {
                        this.selectedIndex = -1;
                    } else {
                        this.toggleSearch();
                    }
                    break;
            }
        },

        scrollToSelected() {
            this.$nextTick(() => {
                const items = this.$refs.resultItems || this.$refs.historyItems;
                if (items && items[this.selectedIndex]) {
                    items[this.selectedIndex].scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                }
            });
        },

        performSearch: debounce(async function () {
            // Re-validate in case input changed after debounce was scheduled
            if (this.searchTarget.length < 4) {
                this.loading = false;
                return;
            }

            this.oldSearchTarget = this.searchTarget;
            this.searchResults = [];
            this.loading = true;
            this.requestClearWordsHighlight(true);

            try {
                const response = await searchAPI.search({
                    search: this.searchTarget,
                    issueId: this.issue.id,
                });
                this.searchResults = response.results;
                if (this.searchResults.length > 0) {
                    this.addToSearchHistory(this.searchTarget);
                    this.selectedIndex = -1;
                }
            } catch (e) {
                console.error('error', e);
            }
            this.loading = false;
        }, 1000),

        goToPageOrLocation(result) {
            this.isEpub
                ? this.goToLocation({
                      chapterHref: result.location.value,
                      trigger: 'click',
                      source: 'notes',
                  })
                : this.goToPage({
                      page: result.location.value,
                      trigger: 'click',
                      source: 'search',
                  });
            this.requestQueryWordsHighlight({
                page: result.location.value,
                queryWords: result.queryWords,
            });
            this.toggleSearch();
        },
    },
};
</script>
